(ns io.daskul.api-contract.filters
  "Wire contract for how list filters are addressed as query params (#653).

   A `{:field :date :type :date-range}` filter is not addressed by its field
   name — it expands into a pair of bound params derived from that name:

     ?date-from=2026-07-27&date-to=2026-08-02

   This namespace is the single source of truth for that derivation, and is
   .cljc because both sides of the wire consume it: the API generator builds
   the accepted-param whitelist from `range-field-lookup`, and the webapp
   builds outgoing requests from `range-param`.

   The suffix is written exactly once. Every other form — the server's reverse
   lookup, the client's outgoing param keys — is derived, so changing the
   convention changes both sides together.

   That is the whole point: #590 replaced the Session aggregate's bespoke
   `from`/`to` filters with the framework's date-range filter, which renamed the
   params to `date-from`/`date-to`. The API moved; the calendar's week view kept
   sending `?from`/`?to`, and query-param validation rejected every request as a
   disallowed key — the week view was blank on staging until #648. Two hand-
   maintained copies of one convention is exactly the drift this removes."
  (:require
   [clojure.string :as str]))

(def bounds
  "The two bounds of a range filter, in [lower upper] order.

   Order is meaningful — `range-params` returns keys in it, so callers can
   destructure [from-key to-key] positionally."
  [:from :to])

(defn range-param
  "Query-param key addressing one `bound` of `field`'s date-range filter.

     (range-param :date :from) => :date-from

   The only place the suffix is applied."
  [field bound]
  (keyword (str (name field) "-" (name bound))))

(defn range-params
  "Both param keys for `field`, ordered by `bounds`.

     (range-params :date) => [:date-from :date-to]"
  [field]
  (mapv #(range-param field %) bounds))

(defn range-field-lookup
  "Reverse map for the server: every bound param key → what it selects.

     (range-field-lookup [:date])
     => {:date-from {:field :date :bound :from}
         :date-to   {:field :date :bound :to}}

   Carrying `:bound` means the server never re-parses the suffix off the key it
   just derived — the string form is produced in one direction only."
  [fields]
  (into {}
        (mapcat (fn [field]
                  (map (fn [bound]
                         [(range-param field bound) {:field field :bound bound}])
                       bounds)))
        fields))

(defn range-param?
  "True when `param-key` addresses a bound of one of `fields`."
  [fields param-key]
  (contains? (range-field-lookup fields) param-key))

(defn strip-bound
  "The field name a bound param addresses, or nil if it isn't one.

     (strip-bound :date-from) => :date

   Parsing helper for callers holding a param key with no field list to check
   against. Prefer `range-field-lookup` when the fields are known."
  [param-key]
  (let [s (name param-key)]
    (some (fn [bound]
            (let [suffix (str "-" (name bound))]
              (when (and (str/ends-with? s suffix)
                         (> (count s) (count suffix)))
                (keyword (subs s 0 (- (count s) (count suffix)))))))
          bounds)))
